import { useAccordionItemContext, useDisclosure } from '@chakra-ui/react';
import {
  AdditionalFrequency,
  IOfferingRes,
  IProduct,
  IRateReqSchema,
  IRateReqSchemaUI,
  OfferingTypesEnum,
  RateStatusEnum,
} from '@monetize/types/app';
import { ComponentProps, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  MAccordion,
  MAccordionItem,
  MBox,
  MSkeleton,
} from '../../../../../components/Monetize';
import { useOfferingRate } from '../../../../../hooks';
import { useCurrencies } from '../../../../../hooks/useCurrencies';
import { logger } from '../../../../../services/logger';
import { transformFormDataToStatusUpdate } from '../../../../../utils/rateFormUtils';
import { convertRateResToReq } from '../../../../../utils/rates';
import { AdditionalFrequencyModal } from './AdditionalFrequencyModal';
import {
  createRateFormHandlers,
  useRateFormHandlers,
} from './hooks/useRateFormHandlers';
import { useRateFormState } from './hooks/useRateFormState';
import {
  createAdditionalFrequencyData,
  prepareFormSubmission,
  prepareRatePayload,
  useRateFormValidation,
} from './hooks/useRateFormSubmit';
import { RateFormCollapsed } from './RateFormCollapsed';
import { RateFormExpanded } from './RateFormExpanded';

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
type RateFormAccordionBodyProps = ComponentProps<typeof RateFormCollapsed> &
  ComponentProps<typeof RateFormExpanded>;

const RateFormAccordionBody = (props: RateFormAccordionBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      {!isExpanded && <RateFormCollapsed {...props} />}
      {isExpanded && <RateFormExpanded {...props} />}
    </>
  );
};

export interface RateFormProps {
  id: string;
  products: IProduct[];
  type: OfferingTypesEnum;
  typeId: string;
  archivedOffering?: boolean;
  isClonedRate: boolean;
  handleCloneRate: (data: IRateReqSchemaUI) => void;
  handleDirtyRateIds: (rateId: string, type: 'add' | 'remove') => void;
  onChange?: (rateId: string) => void;
  onRemove?: () => void;
  removeRateTempUUId: (savedTempRateId: string) => void;
  isReadOnly?: boolean;
  isSubscriptionProductTypeExist: boolean;
  isSubscription: boolean;
  isExpandedAll?: boolean;
  offering?: IOfferingRes;
}

const RateForm = ({
  id,
  products,
  type,
  typeId,
  archivedOffering,
  isClonedRate,
  handleCloneRate,
  handleDirtyRateIds,
  onChange,
  removeRateTempUUId,
  onRemove,
  isReadOnly,
  isSubscriptionProductTypeExist,
  isSubscription,
  isExpandedAll = false,
  offering,
}: RateFormProps) => {
  const {
    control,
    watch,
    handleSubmit,
    getValues,
    setValue,
    reset,
    resetField,
    trigger,
    setError,
    formState: { isSubmitted, isDirty, errors },
  } = useFormContext<IRateReqSchemaUI>();

  const [
    isAdditionalFrequencyRateLoading,
    setIsAdditionalFrequencyRateLoading,
  ] = useState<boolean>(false);
  const [pricesToCopyFromProductId, setPricesToCopyFromProductId] = useState<
    string | undefined
  >();

  const additionalFrequencyModal = useDisclosure();
  const { defaultCurrency } = useCurrencies();
  const values = getValues();
  const {
    id: rateId,
    name,
    billingFrequency,
    billingFrequencyInMonths,
    usageBillingFrequency,
    subscriptionTiming,
    currency,
  } = values;

  const {
    createOfferingRate,
    updateOfferingRate,
    fetchOfferingRate,
    removeOfferingRate,
    setOfferingRate,
    offeringRate,
    loading,
    isFetching,
  } = useOfferingRate(typeId);

  const {
    nameRef,
    expandedValue,
    setExpandedValue,
    isNewRate,
    isLocked,
    isCreditAllocationFrequencyRequired,
    shouldShowSkeleton,
  } = useRateFormState({
    id,
    products,
    offeringRate,
    isClonedRate,
    isExpandedAll,
    name,
    isFetching,
  });

  const { validateForm } = useRateFormValidation({
    offeringType: type,
    isCreditAllocationFrequencyRequired,
    setError,
  });

  useRateFormHandlers({
    id,
    rateId,
    products,
    type,
    defaultCurrency,
    isNewRate,
    isClonedRate,
    isDirty,
    offeringRate,
    setValue,
    reset,
    fetchOfferingRate,
    handleDirtyRateIds,
  });

  const handleAdditionalFrequency = async (
    frequency: AdditionalFrequency,
    finalData: IRateReqSchema,
  ) => {
    const additionalFrequencyData = createAdditionalFrequencyData(
      products,
      frequency,
      finalData,
    );

    const additionalOfferRateRes = await createOfferingRate(
      additionalFrequencyData,
    );

    if (additionalOfferRateRes) {
      setOfferingRate(additionalOfferRateRes);
      onChange && onChange(additionalOfferRateRes.id);
    }
  };

  const onSubmit = async (formData: IRateReqSchemaUI) => {
    setIsAdditionalFrequencyRateLoading(true);

    // Validate form data
    if (!validateForm(formData)) {
      setIsAdditionalFrequencyRateLoading(false);
      return;
    }

    // Transform form data to API payload
    const finalData = prepareFormSubmission(formData, products, type);
    const payload = prepareRatePayload(finalData, 'RATE_FORM');
    const createNewOfferRateRes = offeringRate
      ? await updateOfferingRate(payload, offeringRate.id)
      : await createOfferingRate(payload);

    // handles newly saved rate
    if (!offeringRate && createNewOfferRateRes) {
      setOfferingRate(createNewOfferRateRes);
      onChange && onChange(createNewOfferRateRes.id);
      removeRateTempUUId(id);
      // Since we are creating a new rate and we are using UUID
      // we should also remove the id from the dirtyRateIds array
      handleDirtyRateIds(id, 'remove');
    }

    if (offeringRate && createNewOfferRateRes) {
      // Since we are updating an existing rate we should also remove the
      // offering rate id from the dirtyRateIds array
      handleDirtyRateIds(offeringRate.id, 'remove');
      reset(
        convertRateResToReq(
          createNewOfferRateRes,
          products,
          defaultCurrency,
          type,
        ),
      );
    }

    if (Array.isArray(formData.additionalFrequencies)) {
      for (const frequency of formData.additionalFrequencies) {
        await handleAdditionalFrequency(frequency, finalData);
      }
    }

    if (additionalFrequencyModal.open) {
      additionalFrequencyModal.onClose();
    }

    setIsAdditionalFrequencyRateLoading(false);
    return createNewOfferRateRes;
  };

  const { handleReset, handleClone, handleRemove } = createRateFormHandlers({
    values,
    offeringRate,
    handleCloneRate,
    onRemove,
    handleDirtyRateIds,
    id,
    reset,
    removeOfferingRate,
  });

  const onError = (error: any) => {
    logger.error(error);
  };

  const handleSaveOrUpdate = () => {
    handleSubmit(onSubmit, onError)();
  };

  const handleSavePlusCreateOtherFrequencies = async () => {
    const result = await trigger();

    if (result) {
      additionalFrequencyModal.onOpen();
    } else {
      onError(errors);
    }
  };

  const handleStatusChange = async (status: RateStatusEnum) => {
    if (offeringRate) {
      const reqData = transformFormDataToStatusUpdate(
        values,
        status,
        products,
        type,
      );
      const payload = prepareRatePayload(reqData, 'RATE_STATUS_UPDATE');
      await updateOfferingRate(payload, offeringRate.id);
    }
  };

  const handleCloseAdditionalFrequencyModal = () => {
    resetField('additionalFrequencies');
    additionalFrequencyModal.onClose();
  };

  const isRateInactive = watch('status') === RateStatusEnum.INACTIVE;
  const showUnsavedAlert = !isSubmitted && !loading && isDirty;

  if (shouldShowSkeleton) {
    return (
      <MBox pl={8} mb={4}>
        <MSkeleton h={14} p={4} />
      </MBox>
    );
  }

  return (
    <>
      <MAccordion
        collapsible
        key={id}
        pl={8}
        mb={4}
        value={expandedValue}
        defaultValue={isExpandedAll ? ['0'] : []}
        onValueChange={({ value }) => setExpandedValue(value)}
      >
        <MAccordionItem
          value="0"
          id={id}
          w="100%"
          minW="fit-content"
          data-testid="rate-form"
        >
          <RateFormAccordionBody
            name={name}
            currency={currency}
            billingFrequency={billingFrequency}
            billingFrequencyInMonths={billingFrequencyInMonths}
            type={type}
            isRateInactive={isRateInactive}
            showUnsavedAlert={showUnsavedAlert}
            isLocked={isLocked}
            isReadOnly={isReadOnly}
            id={id}
            offeringRate={offeringRate}
            handleClone={handleClone}
            handleRemove={handleRemove}
            handleStatusChange={handleStatusChange}
            products={products}
            offering={offering}
            subscriptionTiming={subscriptionTiming}
            usageBillingFrequency={usageBillingFrequency}
            isSubscription={isSubscription}
            isSubscriptionProductTypeExist={isSubscriptionProductTypeExist}
            isCreditAllocationFrequencyRequired={
              isCreditAllocationFrequencyRequired
            }
            archivedOffering={archivedOffering}
            loading={loading}
            handleReset={handleReset}
            handleSaveOrUpdate={handleSaveOrUpdate}
            handleSavePlusCreateOtherFrequencies={
              handleSavePlusCreateOtherFrequencies
            }
            nameRef={nameRef}
            defaultCurrency={defaultCurrency}
            pricesToCopyFromProductId={pricesToCopyFromProductId}
            setPricesToCopyFromProductId={setPricesToCopyFromProductId}
            isDirty={isDirty}
          />
        </MAccordionItem>
      </MAccordion>
      {type !== OfferingTypesEnum.ONETIME && (
        <AdditionalFrequencyModal
          billingFrequency={billingFrequency}
          billingFrequencyInMonths={billingFrequencyInMonths}
          control={control}
          isOpen={additionalFrequencyModal.open}
          onClose={handleCloseAdditionalFrequencyModal}
          errors={errors}
          handleSaveOrUpdate={handleSaveOrUpdate}
          isAdditionalFrequencyRateLoading={isAdditionalFrequencyRateLoading}
        />
      )}
    </>
  );
};

export default RateForm;
